import type { UseQueryResult } from "@tanstack/react-query";
import { Card } from "~/components/Card";
import { DetailsLayout, LockCard } from "~/components/DetailsCards";
import type { IngestionPart } from "~/lqs";
import {
  LqsHistoryCard,
  LqsManageCard,
  LqsResourceFields,
  useDeleteIngestionPart,
  useIngestionPart,
  useUpdateIngestionPart,
} from "~/lqs";
import { calculateProcessRefetchInterval, Process } from "~/lqs/process";
import { lqsRoutePaths, useLqsNavigator, useTypedParams } from "~/paths";
import { selectData } from "~/utils";

export function IngestionPartDetails() {
  const { ingestionId, ingestionPartId } = useTypedParams(
    lqsRoutePaths.INGESTION_PART_DETAILS,
  );

  const lqsNavigator = useLqsNavigator({
    toIngestionPartEdit: true,
    toIngestionPartTable: true,
  });

  const query = useIngestionPart(ingestionId, ingestionPartId, {
    select: selectData,
    refetchInterval: calculateProcessRefetchInterval,
  });

  const generalSection = <GeneralSection query={query} />;
  const processSection = (
    <Process
      query={query}
      mutation={useUpdateIngestionPart(ingestionId, ingestionPartId)}
    />
  );
  const lockSection = (
    <LockCard
      resourceName="ingestion part"
      query={query}
      updateMutation={useUpdateIngestionPart(ingestionId, ingestionPartId)}
    />
  );
  const historySection = <LqsHistoryCard query={query} />;
  const manageSection = (
    <LqsManageCard
      resourceName="ingestion part"
      query={query}
      editLocation={lqsNavigator.toIngestionPartEdit({
        ingestionId,
        ingestionPartId,
      })}
      deleteMutation={useDeleteIngestionPart(ingestionId, ingestionPartId)}
      getReadableName={(ingestionPart) => ingestionPart.id}
      listLocation={lqsNavigator.toIngestionPartTable({ ingestionId })}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {processSection}
        </>
      }
      secondaryGridColumn={
        <>
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {processSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<IngestionPart> }) {
  return (
    <Card title="General">
      <LqsResourceFields
        registryKey="ingestionPart"
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          {
            dataType: "foreign-key",
            resourceType: "ingestion",
            accessor: "ingestionId",
          },
          { dataType: "number", accessor: "sequence" },
          { dataType: "json", accessor: "context" },
        ]}
      />
    </Card>
  );
}
